<template>
  <div>
    <div class="alert alert-danger mt-10" role="alert"
         v-show="hasErrors()">
      <b>Please correct the following errors:</b>
      <ul>
        <li v-for="error in errorsList">{{ error }}</li>
      </ul>
    </div>

    <div class="mt-20">

      <div class="mx-10 mb-20" style="max-width: 580px">
        <div class="form-group row">
          <label for="issue_date" class="col-md-2 form-control-label required">Invoice Date</label>
          <div class="col-md-6">
            <DatePicker reference="issue_date"
                        :required="true"
                        :on-change="onDateChanged"
                        :error="errors.issue_date"
                        :value="moment(invoice.issue_date, 'YYYY-MM-DD hh:mm:ss').toDate()"/>
          </div>
        </div>

        <div class="form-group row">
          <label for="paid_date" class="col-md-2 form-control-label">Paid Date</label>
          <div class="col-md-6">
            <DatePicker reference="paid_date"
                        :extras="{ endDate: '0d' }"
                        :on-change="onDateChanged"
                        :error="errors.paid_date"
                        :value="invoice.paid_date ? moment(invoice.paid_date, 'YYYY-MM-DD hh:mm:ss').toDate() : null"/>
          </div>
        </div>
      </div>

      <table id="invoice-entries" class="table data-table dataTable table-bordered table-striped table-responsive border-0">
        <thead>
        <tr>
          <th class="w-40">SL</th>
          <th>Item</th>
          <th>Billing Month/Date</th>
          <th>Description</th>
          <th>Amount</th>
          <th class="w-40">Action</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(entry, index) in invoice.entries" :key="index">
          <td class="text-center">{{ index + 1 }}</td>
          <td>
            <select class="form-control item-input" required="required"
                    v-model="entry.item_id"
                    @change="onItemChanged(entry, index)"
                    v-bind:class="{ 'is-invalid' : errors.entries[index]?.item_id ?? false }">
              <option value="" disabled>Select...</option>
              <option v-for="item in items"
                      :value="item.id"
                      :selected="entry.item_id === item.id">
                {{ item.name }}
              </option>
            </select>
            <small class="invalid-feedback d-block"
                   v-show="(errors.entries[index]?.item_id ?? '') !== ''">
              {{ errors.entries[index]?.item_id || '' }}
            </small>
          </td>
          <td>
              <DatePicker :ref="`due_for_${index}`"
                          :reference="`due_for_${index}`"
                          :on-change="onDueForChanged"
                          :value="moment(entry.due_for, 'YYYY-MM-DD')?.toDate() || null"
                          date-format="YYYY-MM-DD"
                          :picker-format="entry.datepickerOptions?.pickerFormat"
                          :view-mode="entry.datepickerOptions?.viewMode"
                          :min-view-mode="entry.datepickerOptions?.viewMode"
                          :error="errors.entries[index]"/>
              <small class="invalid-feedback d-block"
                     v-show="(errors.entries[index]?.due_for ?? '') !== ''">
                  {{ errors.entries[index]?.due_for || '' }}
              </small>
          </td>
          <td>
            <input class="form-control desc-input" v-model="entry.description"
                   v-bind:class="{ 'is-invalid' : errors.entries[index]?.description ?? false }"/>
            <small class="invalid-feedback d-block"
                   v-show="(errors.entries[index]?.description ?? '') !== ''">
              {{ errors.entries[index]?.description || '' }}
            </small>
          </td>
          <td>
            <input class="form-control amount-input text-right" v-model="entry.amount"
                   v-bind:class="{ 'is-invalid' : errors.entries[index]?.amount ?? false }"
                   @focus="e => e.target.select()"
                   @keyup="calculateTotalAmount"/>
            <small class="invalid-feedback d-block"
                   v-show="(errors.entries[index]?.amount ?? '') !== ''">
              {{ errors.entries[index]?.amount || '' }}
            </small>
          </td>
          <td>
            <ActionButtons :entry="entry"
                           :index="index"
                           :remove-entry="removeEntry"
                           :has-delete-permission="this.hasDeletePermission"/>
          </td>
        </tr>
        </tbody>
        <tfoot>
        <tr>
          <th colspan="4" class="text-right font-weight-bolder">Total</th>
          <th class="text-right pr-20">{{ invoice.total_amount }}</th>
          <th></th>
        </tr>
        </tfoot>
      </table>

      <button type="button" id="add-row-btn" class="d-block btn btn-outline btn-primary mt-10" @click="addEntry">
        <i class="fa fa-plus"></i>
        <span class="text ml-5">Add Item</span>
      </button>

      <div id="send-block" class="mt-30" v-if="sendSmsEnabled || sendEmailEnabled">
        <div class="d-flex">
          <!-- Send SMS -->
          <div v-if="sendSmsEnabled">
            <div class="abc-checkbox abc-checkbox-circle abc-checkbox-success">
              <input name="send_sms" id="send-sms" type="checkbox" v-model="sendSms"/>
              <label for="send-sms" class="pl-20" style="padding-top: 8px">
                Send SMS
              </label>
            </div>
          </div>
          <!-- Send Email -->
          <div v-if="sendEmailEnabled">
            <div class="abc-checkbox abc-checkbox-circle abc-checkbox-success">
              <input name="send_email" id="send-email" type="checkbox" v-model="sendEmail"/>
              <label for="send-email" class="pl-20" style="padding-top: 8px">
                Send Email
              </label>
            </div>
          </div>
        </div>
      </div>

      <button type="submit" class="d-block ladda-button btn btn-primary mt-20"
              data-app-plugin="ladda" data-style="expand-left"
              @click="submitInvoice">
        Submit
      </button>

    </div>
  </div>
</template>

<script>
import {ref} from 'vue';
import FormElement from '../../components/FormElement';
import DatePicker from "../../components/DatePicker";
import ActionButtons from "./components/ActionButtons";
import moment from "moment";

export default {
  components: {
    FormElement,
    DatePicker,
    ActionButtons,
  },
  props: {
    invoiceCreateUrl: {
      type: String,
      required: true,
    },
    invoiceUpdateUrl: {
      type: String,
      required: true,
    },
    invoiceDetailsUrl: {
      type: String,
      required: true,
    },
    isEdit: {
      type: Boolean,
      required: true,
    },
    items: {
      type: Object,
      required: true,
    },
    invoice: {
      type: Object,
      required: true,
    },
    sendSmsEnabled: {
      type: Boolean,
      required: true,
    },
    sendEmailEnabled: {
      type: Boolean,
      required: true,
    },
    hasDeletePermission: {
      type: Boolean,
      required: true,
    }
  },
  setup(props) {
    return {
      invoice: ref({
        id: props.invoice.id,
        issue_date: props.invoice.issue_date,
        paid_date: props.invoice.paid_date,
        total_amount: props.invoice.total_amount ? Number(props.invoice.total_amount).toLocaleString() : 0,
        entries: props.invoice.id ? props.invoice.entries
            .map(entry => { entry.amount = Number(entry.amount); return entry }) : [],
      }),
      sendSms: ref(props.sendSmsEnabled),
      sendEmail: ref(props.sendEmailEnabled),
      errors: ref({
        issue_date: null,
        paid_date: null,
        entries: {},
      }),
      errorsList: ref([]),
    }
  },
  methods: {
    moment(dateString, format) {
      moment.locale('en')
      return dateString ? moment(dateString, format ? format : 'DD-MM-YYYY') : null
    },
    initialErrorsState() {
      return {
        issue_date: null,
        paid_date: null,
        entries: [],
      }
    },
    onDateChanged(ref, date) {
      if (ref === 'issue_date') {
        this.invoice.issue_date = date
      } else if (ref === 'paid_date') {
        this.invoice.paid_date = date
      }
    },
    calculateTotalAmount() {
      this.invoice.total_amount = this.invoice.entries.map(entry => entry.amount ? Number(entry.amount) : 0)
          .reduce((a, b) => a + b, 0)
          .toLocaleString()
    },
    addEntry() {
      this.invoice.entries.push({
        id: null,
        item_id: null,
        due_for: null,
        description: null,
        amount: null,
      })
      this.$nextTick(() => {
        $('.selectpicker').eq(this.invoice.entries.length - 1).selectpicker('refresh')
      })
    },
    removeEntry(index) {
      this.invoice.entries.splice(index, 1)
      this.calculateTotalAmount()
      this.$nextTick(() => $('.selectpicker').selectpicker('refresh'))
    },
    onItemChanged(entry, index) {
        const itemId = entry.item_id
        const format = itemId === 1 || itemId === 8 ? 'M yyyy' : 'dd-mm-yyyy'
        const viewMode = itemId === 1 || itemId === 8 ? 'months' : 'days'

        this.invoice.entries[index].datepickerOptions = {
            pickerFormat: format,
            viewMode: viewMode
        }

        this.$nextTick(() => this.$refs[`due_for_${index}`][0].forceUpdate())
    },
    onDueForChanged(ref, date) {
        this.invoice.entries[ref.replace('due_for_', '')].due_for = date
    },
    stopLoaders() {
      setTimeout(() => window.Ladda.stopAll(), 500)
    },
    hasErrors() {
      return this.errorsList.length > 0
    },
    async submitInvoice() {
      this.clearErrors()

      if (document.querySelector('#school').value === '') {
        toastr.error('Please select a school.')
        this.stopLoaders()

        return
      }

      if (document.querySelector('#status').value === '') {
        toastr.error('Please select a status.')
        this.stopLoaders()

        return
      }

      let entries = [];
      for (let i = 0; i < this.invoice.entries.length; i++) {
        const entry = this.invoice.entries[i]
        entries.push({
          id: entry.id,
          item_id: entry.item_id,
          due_for: entry.due_for,
          description: entry.description,
          amount: entry.amount,
        })
      }

      const response = await fetch(
          this.isEdit ? this.invoiceUpdateUrl : this.invoiceCreateUrl,
          {
            method: this.isEdit ? 'put' : 'post',
            body: JSON.stringify({
              school_id: document.querySelector('#school').value,
              branch_id: document.querySelector('#branch_id').value,
              student_count: document.querySelector('#student_count').value,
              status: document.querySelector('#status').value,
              issue_date: this.invoice.issue_date,
              paid_date: this.invoice.paid_date,
              total_amount: this.invoice.total_amount,
              send_sms: this.sendSms,
              send_email: this.sendEmail,
              entries
            }),
            headers: new Headers({
              'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
              'X-Requested-With': 'XMLHttpRequest',
              'Content-Type': 'application/json'
            }),
            credentials: 'include'  // adds cookie header
          }
      )

      if (response.status === 422) {
        const json = await response.json()
        this.showSubmissionErrors(this.transformSubmissionErrors(json.errors))
      } else if (!response.ok) {
        const error = await response.text()
        toastr.error(`Failed saving invoice: ${error}`)
      } else {
        toastr.success('Invoice saved successfully')
        if (this.isEdit) {
          window.location = this.invoiceDetailsUrl.replace('0', this.invoice.id)
        } else {
          const json = await response.json()
          window.location = this.invoiceDetailsUrl.replace('0', json.id)
        }
      }

      this.stopLoaders()
    },
    transformSubmissionErrors(serverSideErrors) {
      const errors = this.initialErrorsState()
      let errorsList = []

      for (let prop in serverSideErrors) {
        const propParts = prop.split('.')
        if (propParts.length === 3) {
          if (!errors.entries.hasOwnProperty(propParts[1])) {
            errors.entries[propParts[1]] = {}
          }
          errors.entries[propParts[1]][propParts[2]] = serverSideErrors[prop][0]
        } else {
          errors[prop] = serverSideErrors[prop][0]
        }

        errorsList.push(serverSideErrors[prop][0])
      }

      this.errorsList = errorsList

      return errors
    },
    showSubmissionErrors(errors) {
      console.log(errors)
      this.errors = errors
      toastr.error(`Please correct the errors specified.`)
      window.scrollTo({top: 0, behavior: 'smooth'})
    },
    clearErrors() {
      this.errors = this.initialErrorsState()
      this.errorsList = []
    }
  },
  async mounted() {
    await this.$nextTick()
    if (!this.invoice.id) {
      this.addEntry()
    }

    this.invoice.entries.forEach((entry, index) => this.onItemChanged(entry, index))

    Ladda.bind('.ladda-button')
  },
}
</script>

<style>
#invoice-entries th,
#invoice-entries td {
  text-align: center;
  vertical-align: top;
}

#invoice-entries thead td {
  background-color: #4E9EFF;
  border-top: 1px solid #3E8EF7 !important;
  border-bottom: 1px solid #3E8EF7 !important;
  color: #FFF !important;
  font-size: 16px;
  font-weight: 500!important;
}

#invoice-entries .item-input {
  min-width: 200px;
}
#invoice-entries .desc-input {
  min-width: 300px;
}
#invoice-entries .amount-input {
  min-width: 100px;
}

#invoice-entries tfoot th {
  font-weight: 600 !important;
  font-size: 20px;
}
</style>
